import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Box, Paper, Typography, TextField, Button, Stack, CircularProgress, Alert, Link } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import {
  getVaultAccessStatus, setVaultPassword, verifyVaultPassword, requestVaultReset,
} from "../../services/vault.service";

// Wraps the Vault section (masters + records via <Outlet/>). Asks for the password EVERY time
// the user enters the vault, but NOT while navigating masters↔records. Leaving clears the token.
// Forgot-password (users only): request → admin approves → user sets a new password.
export default function VaultGate() {
  const isAdmin = (() => {
    try {
      const d = jwtDecode(localStorage.getItem("token"));
      return d.role === 1 || d.role === 4;
    } catch {
      return false;
    }
  })();

  const [unlocked, setUnlocked] = useState(false);
  const [mode, setMode] = useState("loading"); // loading | set | enter | pending
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const checkStatus = async () => {
    setErr("");
    try {
      const res = await getVaultAccessStatus();
      const { hasPassword, resetStatus } = res?.data || {};
      if (resetStatus === "pending") setMode("pending");
      else if (resetStatus === "approved") setMode("set"); // admin approved → set new (no old)
      else setMode(hasPassword ? "enter" : "set");
    } catch {
      setMode("enter");
    }
  };

  useEffect(() => {
    checkStatus();
    return () => sessionStorage.removeItem("vaultToken");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = (token) => {
    sessionStorage.setItem("vaultToken", token);
    setUnlocked(true);
  };

  const handleSet = async () => {
    setErr("");
    if (pwd.length < 4) return setErr("Password must be at least 4 characters");
    if (pwd !== confirm) return setErr("Passwords do not match");
    setBusy(true);
    try {
      const res = await setVaultPassword({ password: pwd });
      unlock(res.data.token);
    } catch (e) {
      setErr(e?.message || "Failed to set password");
    } finally {
      setBusy(false);
    }
  };

  const handleEnter = async () => {
    setErr("");
    setBusy(true);
    try {
      const res = await verifyVaultPassword({ password: pwd });
      unlock(res.data.token);
    } catch (e) {
      setErr(e?.message || "Incorrect password");
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async () => {
    setErr("");
    setBusy(true);
    try {
      await requestVaultReset();
      setMode("pending");
    } catch (e) {
      setErr(e?.message || "Failed to send request");
    } finally {
      setBusy(false);
    }
  };

  if (unlocked) return <Outlet />;

  // Reset request pending admin approval
  if (mode === "pending") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <Paper sx={{ p: 4, width: 400, textAlign: "center" }}>
          <HourglassEmptyIcon sx={{ fontSize: 44, color: "#d97706" }} />
          <Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>Approval pending</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your password reset request was sent to the admin. Once approved, you can set a new password.
          </Typography>
          <Button variant="outlined" onClick={checkStatus}
            sx={{ borderColor: "#008080", color: "#008080" }}>Check again</Button>
        </Paper>
      </Box>
    );
  }

  const isSet = mode === "set";
  return (
    <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
      <Paper sx={{ p: 4, width: 380, textAlign: "center" }}>
        <LockIcon sx={{ fontSize: 44, color: "#008080" }} />
        <Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>
          {mode === "loading" ? "Vault" : isSet ? "Set your Vault password" : "Enter Vault password"}
        </Typography>
        {mode !== "loading" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isSet ? "Create a password to protect your vault." : "This vault is locked. 🔒"}
          </Typography>
        )}
        {mode === "loading" ? (
          <Box sx={{ py: 3 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField type="password" label="Password" value={pwd} autoFocus
              onChange={(e) => setPwd(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !isSet && handleEnter()} />
            {isSet && (
              <TextField type="password" label="Confirm password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSet()} />
            )}
            {err && <Alert severity="error">{err}</Alert>}
            <Button variant="contained" disabled={busy} onClick={isSet ? handleSet : handleEnter}
              sx={{ bgcolor: "#008080", "&:hover": { bgcolor: "#006666" } }}>
              {busy ? "..." : isSet ? "Set & Open" : "Unlock"}
            </Button>
            {/* Forgot password — users only (admins manage their own) */}
            {!isSet && !isAdmin && (
              <Link component="button" type="button" underline="hover" disabled={busy}
                onClick={handleForgot} sx={{ color: "#6b7280", fontSize: 13 }}>
                Forgot password?
              </Link>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
